import { Button, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DynamicForm, GenericDialog, validateEmail, validatePhone } from 'src/components';
import { PHONE_FIELD_PLACEHOLDER } from 'src/components/Forms/const';
import { Client, usePhonesAndEmailsLabels, useSaveClientWithRelatedData } from 'src/graphql';
import { NullableDeepPartial } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { ClientRaw } from 'src/types/api-types';
import { useNotifications } from 'src/utils/hooks';
import { ClientFormDynamicFieldsName } from './client-fields';
import { DynamicPropertyElement, DynamicPropertyItem } from './dynamic-property-element';
import { useNewClientForm } from './use-new-client-form';

export const AddClientModal = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { closeModal } = useModalMethods(Modals.AddClientModal);
  const { isOpened, modalProps } = useModalState(Modals.AddClientModal);

  const { showSuccessAlert, showErrorAlert } = useNotifications();

  const { client, address, participant, conversation, onSuccessSubmit, onCloseModal } =
    modalProps ?? {};

  const partnerId =
    modalProps?.partnerId ??
    conversation?.partner?.partnerId ??
    client?.partner?.id ??
    client?.partnerId ??
    address?.partner?.id ??
    address?.partnerId ??
    selectedPartnerId;

  const { clientEmailLabels, clientPhoneLabels } = usePhonesAndEmailsLabels();

  const { newClientFormFields } = useNewClientForm(partnerId);

  const { saveClientWithRelatedData, isLoading } = useSaveClientWithRelatedData();

  const formPhoneNumbers = useMemo(
    () =>
      client?.phoneNumbers
        ?.map(
          (phone, index) =>
            ({
              value: phone.number?.toString(),
              valueId: phone.id,
              labelId: phone.labelId,
              isPrimary:
                phone.id === client.primaryClientPhoneNumberId ||
                (!client.primaryClientPhoneNumberId && index === 0),
            }) as DynamicPropertyItem,
        )
        .sort((phone) => (phone.isPrimary ? -1 : 1)) ?? [],
    [client?.phoneNumbers, client?.primaryClientPhoneNumberId],
  );

  const formEmails = useMemo(
    () =>
      client?.emails
        ?.map(
          (email, index) =>
            ({
              value: email.email,
              valueId: email.id,
              labelId: email.labelId,
              isPrimary:
                email.id === client.primaryClientEmailId ||
                (!client.primaryClientEmailId && index === 0),
            }) as DynamicPropertyItem,
        )
        .sort((email) => (email.isPrimary ? -1 : 1)) ?? [],
    [client?.emails, client?.primaryClientEmailId],
  );

  const formInitialValues = useMemo(
    () =>
      client
        ? ({
            ...(client as Client),
            marketingOptOut: (client as Client).marketingOptOut ?? false,
          } as Client)
        : undefined,
    [client],
  );

  const formMethods = useForm<Record<ClientFormDynamicFieldsName, DynamicPropertyItem[]>>({
    shouldFocusError: true,
    mode: 'all',
    values: {
      phoneNumbers: formPhoneNumbers,
      emails: formEmails,
    },
  });

  const [phoneNumbers, setPhoneNumbers] = useState<DynamicPropertyItem[]>([]);
  const [emails, setEmails] = useState<DynamicPropertyItem[]>([]);

  useEffect(() => {
    const { unsubscribe } = formMethods.watch((value, info) => {
      if (info.name?.startsWith(ClientFormDynamicFieldsName.Emails)) {
        setEmails((value.emails ?? []) as DynamicPropertyItem[]);
      }

      if (info.name?.startsWith(ClientFormDynamicFieldsName.PhoneNumbers)) {
        setPhoneNumbers((value.phoneNumbers ?? []) as DynamicPropertyItem[]);
      }
    });

    return () => unsubscribe();
  }, [formMethods]);

  useEffect(() => {
    if (isOpened) {
      formMethods.reset();
    }
  }, [formMethods, isOpened]);

  const dynamicProperties = [
    {
      title: 'Email',
      type: 'email',
      name: ClientFormDynamicFieldsName.Emails,
      icon: 'email',
      labels: clientEmailLabels,
    },
    {
      title: 'Phone',
      type: 'tel',
      name: ClientFormDynamicFieldsName.PhoneNumbers,
      placeholder: PHONE_FIELD_PLACEHOLDER,
      icon: 'phone_iphone',
      labels: clientPhoneLabels,
    },
  ];

  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const triggerSubmit = useCallback(() => {
    submitButtonRef?.current?.click();
  }, []);

  const closeAddClientModal = useCallback(
    (result?: NullableDeepPartial<ClientRaw>) => {
      closeModal();
      onCloseModal?.(result);
      setEmails([]);
      setPhoneNumbers([]);
    },
    [closeModal, onCloseModal],
  );

  const handleAddClient = useCallback(
    async (
      values: Client & {
        partner?: {
          id: number;
        } | null;
      },
    ) => {
      const currentPartnerId = values.partnerId ?? values.partner?.id ?? partnerId;
      const primaryPhoneNumber = phoneNumbers.find((field) => field.isPrimary);
      const primaryEmail = emails.find((field) => field.isPrimary);
      const valuesToUpdate = {
        ...values,
        primaryEmail: primaryEmail?.value,
        primaryClientEmailId: primaryEmail?.valueId,
        primaryEmailLabelId: primaryEmail?.labelId,
        primaryPhoneNumber: primaryPhoneNumber?.value,
        primaryPhoneNumberLabelId: primaryPhoneNumber?.labelId,
        primaryClientPhoneNumberId: primaryPhoneNumber?.valueId,
        emails: emails.map((field) => ({
          id: field.valueId,
          clientEmailId: field.valueId,
          clientId: client?.clientId,
          partnerId: currentPartnerId,
          labelId: field.labelId,
          email: field.value,
        })),
        phoneNumbers: phoneNumbers.map((field) => ({
          id: field.valueId,
          clientPhoneNumberId: field.valueId,
          clientId: client?.clientId,
          partnerId: currentPartnerId,
          labelId: field.labelId,
          number: parseInt(field.value),
        })),
      } as Client;

      const errors = checkClientErrors(valuesToUpdate);
      if (errors) {
        showErrorAlert(errors);
        return;
      }

      const result = await saveClientWithRelatedData({
        ...valuesToUpdate,
        partnerId: currentPartnerId,
        address,
        participant,
      });

      if (!result) {
        return;
      }

      closeAddClientModal(result);
      onSuccessSubmit?.(result);
      showSuccessAlert(client?.clientId ? 'Client was updated' : 'Client was created');
    },
    [
      partnerId,
      phoneNumbers,
      emails,
      closeAddClientModal,
      saveClientWithRelatedData,
      address,
      participant,
      onSuccessSubmit,
      showSuccessAlert,
      client?.clientId,
      showErrorAlert,
    ],
  );

  const actionButtons = useMemo(
    () => (
      <Button
        onClick={triggerSubmit}
        disabled={isLoading}
        sx={{
          color: (theme) => theme.palette.custom.OV.sys.primary,
        }}
      >
        Save
      </Button>
    ),
    [isLoading, triggerSubmit],
  );

  return (
    <GenericDialog
      draggable
      modal={Modals.AddClientModal}
      title={client?.clientId ? 'Update client' : 'Create client'}
      actionButtons={actionButtons}
    >
      <Stack
        sx={{
          paddingTop: 2,
        }}
        gap={2}
      >
        <DynamicForm
          style={{ gap: '1rem' }}
          onSubmit={handleAddClient}
          fields={newClientFormFields}
          value={formInitialValues}
        >
          <Button
            type="submit"
            ref={submitButtonRef}
            sx={{
              display: 'none',
            }}
          >
            Save
          </Button>
        </DynamicForm>

        <FormProvider {...formMethods}>
          {dynamicProperties.map((property) => (
            <DynamicPropertyElement key={property.title} property={property} />
          ))}
        </FormProvider>
      </Stack>
    </GenericDialog>
  );
};

function checkClientErrors(client?: Client) {
  const hasInvalidEmails =
    client?.emails?.some((item, idx, currentArray) => {
      return (
        !validateEmail(item.email) ||
        // check duplicated elements
        currentArray.findIndex((currentItem) => currentItem.email === item.email) !== idx
      );
    }) ?? false;

  if (hasInvalidEmails) {
    return 'Check the client emails. There are empty, invalid or duplicated values';
  }

  const hasInvalidPhones =
    client?.phoneNumbers?.some((item, idx, currentArray) => {
      return (
        !validatePhone(item.number) ||
        // check duplicated elements
        currentArray.findIndex((currentItem) => currentItem.number === item.number) !== idx
      );
    }) ?? false;

  if (hasInvalidPhones) {
    return 'Check the client phones. There are empty, invalid or duplicated values';
  }

  return '';
}
